import { Box, Stack, StackProps } from '@mui/material';
import React from 'react';

/**
 * Places children in an evenly spaced out row that turns into a column for smaller display width.
 *
 * Defaults to spacing and width optimized for page panels.
 * Set the stretch=false to prevent children from being stretched
 */
export const ReactiveRow = ({
  stretch,
  children,
  ...props
}: Omit<StackProps, 'direction' | 'justifyContent'> & { stretch?: boolean }): React.JSX.Element => (
  <Stack
    spacing={2}
    sx={{ width: '100%' }}
    direction={{ xs: 'column', md: 'row' }}
    justifyContent={{ md: 'space-between' }}
    {...props}
  >
    {stretch === false ? children : React.Children.map(children, (c) => <Box width={'100%'}>{c}</Box>)}
  </Stack>
);

/**
 *  Places children in a column, evens out their width and centers them horizontally.
 *  Note that the width and sx props apply to the children and not the container.
 */
export const CenteredColumn = ({
  children,
  ...props
}: Omit<StackProps, 'direction' | 'alignItems'>): React.JSX.Element => (
  <Stack spacing={1} {...props} sx={undefined} width={'100%'} direction={'column'} alignItems={'center'}>
    {React.Children.map(children, (c) => (
      <Box width={props.width} sx={props.sx}>
        {c}
      </Box>
    ))}
  </Stack>
);

/**
 * Like centered column, but with pre-set width to reactively fit into a content panel.
 * Should be used as basis for most panels, unless there are special requirements.
 *
 * @todo types for width properties are not ideal
 */
export const ReactivePanelColumn = ({
  children,
  xsWidth,
  lgWidth,
  ...props
}: Omit<StackProps, 'direction' | 'alignItems'> & {
  xsWidth?: string;
  lgWidth?: string;
}): React.JSX.Element => (
  <Stack width={'98%'} direction={'column'} spacing={2} alignItems={'center'} justifyContent={'flex-start'} {...props}>
    {React.Children.map(children, (c) => (
      <Box sx={{ width: { xs: xsWidth ?? '100%', lg: lgWidth ?? '30%' } }}>{c}</Box>
    ))}
  </Stack>
);
